/* eslint-disable prettier/prettier */
import React, { useMemo, useEffect, useState } from 'react';
import axios from 'axios';
import { BASE_URL_WEB, IMAGE_URL } from '../../../utility';
import PhoneInput from 'react-phone-input-2';
import { Tooltip } from 'antd';
// import { genderArr } from '../../../common/config';
import {
    normalizeAddressValue,
    normalizeExerciseValue,
    normalizeHeightValue,
} from '../../../utility/questionnaireFieldUtils';

export default function MemberQuestionnairePreview() {
    const [formData, setFormData] = useState(null);
    const [formLoading, setFormLoading] = useState(false);
    const [formError, setFormError] = useState('');
    const [responses, setResponses] = useState({ primary: {}, family: [] });
    const [memberData, setMemberData] = useState(null);
    const [showFamilyForm, setShowFamilyForm] = useState(false);
    const [currentFamilyIndex, setCurrentFamilyIndex] = useState(0);
    const [fieldErrors, setFieldErrors] = useState({}); // Add this for error tracking
    //const dispatch = useDispatch();
    const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const companyId = useMemo(() => (queryParams.get('companyId') || '').trim(), [queryParams]);
    const normalizeQuestionId = questionId => String(questionId ?? '').trim().toLowerCase();
    const formAttrOrderMap = useMemo(() => {
        const attr = formData?.formItem?.attr;
        const ids = Array.isArray(attr) ? attr : attr && typeof attr === 'object' ? Object.keys(attr) : [];
        const orderMap = new Map();
        ids.forEach((id, idx) => {
            orderMap.set(normalizeQuestionId(id), idx);
        });
        return orderMap;
    }, [formData]);

    const sortQuestionsByFormAttr = questions => {
        if (!Array.isArray(questions) || questions.length === 0) return [];
        if (!formAttrOrderMap || formAttrOrderMap.size === 0) return questions;
        return questions
            .map((question, originalIndex) => ({
                question,
                originalIndex,
                orderIndex: formAttrOrderMap.get(normalizeQuestionId(question?.id)),
            }))
            // If a question isn't in formItem.attr, don't show it for family members.
            .filter(item => item.orderIndex !== undefined)
            .sort((a, b) => {
                const aOrder = a.orderIndex;
                const bOrder = b.orderIndex;
                if (aOrder === undefined && bOrder === undefined) return a.originalIndex - b.originalIndex;
                if (aOrder === undefined) return 1;
                if (bOrder === undefined) return -1;
                return aOrder - bOrder || a.originalIndex - b.originalIndex;
            })
            .map(({ question }) => question);
    };

    useEffect(() => {
        const fetchRegistrationForm = async () => {
            const currentCompanyId = companyId
            if (!currentCompanyId) {
                setFormError('Company ID is missing.');
                return;
            }
            try {
                setFormLoading(true);
                setFormError('');
                const url = `${BASE_URL_WEB}member-registration-form`;
                const response = await axios.post(url, { companyId: currentCompanyId });
                console.log('Member Registration Form Response:', response);
                setFormData(response?.data || null);
            } catch (error) {
                console.error('Error fetching member registration form:', error);
                const message = error?.response?.data?.message || 'Unable to load the registration form.';
                setFormError(message);
                setFormData(null);
            } finally {
                setFormLoading(false);
            }
        };

        fetchRegistrationForm();
    }, [companyId]);
    console.log('Member Registration Form Data:', companyId, formData);
    const registrationCompany = formData?.companyData || {};
    const companyLogo = useMemo(() => {
        if (!registrationCompany?.imageUrl) return '';
        const logo = registrationCompany.imageUrl;
        return /^https?:\/\//i.test(logo) ? logo : `${IMAGE_URL}${logo}`;
    }, [registrationCompany]);

    const calculateAge = (dob) => {
        if (!dob) return 0;
        const birthDate = new Date(dob);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    };
    const formQuestions = useMemo(() => {
        if (!formData?.formItem?.attr || !formData?.questionArray || !formData?.cpaData) {
            return [];
        }

        return formData.formItem.attr
            .filter(attrId => {
                const fieldData = formData.cpaData.find(item => item.sk === attrId);
                return fieldData && fieldData.PM === true && fieldData.del !== true;
            })
            .map(attrId => {
                const question = formData.questionArray[attrId];
                const fieldData = formData.cpaData.find(item => item.sk === attrId);

                // If CPA marks a question as deleted, hide it.
                if (fieldData?.del === true) return null;

                if (question) {
                    return {
                        id: attrId,
                        question: question,
                        field: fieldData?.field || 'text',
                        helper: fieldData?.helper || '',
                        category: fieldData?.category || 'general',
                        // required: fieldData?.PM || false,
                        ddVal: fieldData?.ddVal || [], // Add this line to include dropdown/multiple-response options
                    };
                }
                return null;
            })
            .filter(item => item !== null);
    }, [formData]);
    console.log('Form Questions:', formQuestions);
    const primaryQuestionIdSet = useMemo(
        () => new Set((formQuestions || []).map(q => normalizeQuestionId(q?.id))),
        [formQuestions]
    );
    const getFamilyQuestionsByAge = (familyMember) => {
        const questionsArray = (formData?.cpaData || [])
            .filter(q => q?.del !== true)
            .map(q => ({
                id: q?.sk,
                question: formData?.questionArray?.[q?.sk] || q?.q || q?.name,
                field: q?.field || 'text',
                helper: q?.helper || '',
                category: q?.category || 'general',
                // required: q.PM || false,
                ddVal: q?.ddVal || [], // Add this line here too
            }));
        const sorted = sortQuestionsByFormAttr(questionsArray);
        console.log('questionsArray', sorted);
        return { questionsArray: sorted };
    };

    const renderFamilyFormField = field => {
        const value = responses.family[currentFamilyIndex]?.[field.id] ?? '';
        const primaryValue = responses.primary?.[field.id];
        const fieldError = fieldErrors[`family_${currentFamilyIndex}_${field.id}`]; // Get error for this field
        console.log('Rendering Family Field:', field, 'with value:', value);
        const isEducationField =
            normalizeQuestionId(field?.id).includes('education') ||
            String(field?.question || '').toLowerCase().includes('education') ||
            String(field?.helper || '').toLowerCase().includes('education');
        const dropdownPlaceholder = 'Select';

        // Get current time in HH:MM format for time fields
        const getCurrentTime = () => {
            const now = new Date();
            return now.toTimeString().slice(0, 5); // Returns "HH:MM"
        };

        // Get current date in YYYY-MM-DD format for date fields
        const getCurrentDate = () => {
            const now = new Date();
            return now.toISOString().split('T')[0]; // Returns "YYYY-MM-DD"
        };

        // Get current date and time in datetime-local format
        const getCurrentDateTime = () => {
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');
            const hours = String(now.getHours()).padStart(2, '0');
            const minutes = String(now.getMinutes()).padStart(2, '0');
            return `${year}-${month}-${day}T${hours}:${minutes}`;
        };

        return (
            <div>
                {(() => {
                    switch (field.field) {
                        case 'dropdown':
                            return (
                                <select className="form-select" id={field.id} value={Array.isArray(value) ? value[0] || '' : value} disabled>
                                    <option value="" disabled={isEducationField} hidden={isEducationField}>
                                        {isEducationField ? '' : dropdownPlaceholder}
                                    </option>
                                    {(field.ddVal || []).map((opt, idx) => {
                                        const optionValue = typeof opt === 'object' && opt !== null ? (opt.value ?? opt.label ?? opt.name ?? '') : opt;
                                        const optionLabel = typeof opt === 'object' && opt !== null ? (opt.label ?? opt.value ?? opt.name ?? '') : opt;
                                        return (
                                            <option key={`${optionValue}_${idx}`} value={optionValue}>
                                                {optionLabel}
                                            </option>
                                        );
                                    })}
                                </select>
                            );
                        case 'multiple-response':
                            return (
                                <div>
                                    {field.ddVal && field.ddVal.map((option, idx) => (
                                        <div key={idx} className="form-check">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                id={`family_${field.id}_${idx}`}
                                                checked={Array.isArray(value) ? value.includes(option) : value === option}
                                                onChange={e => {
                                                    if (e.target.checked) {
                                                        const newValue = Array.isArray(value) ? [...value, option] : [option];
                                                        //handleFamilyChange(field.id, newValue);
                                                    } else {
                                                        const newValue = Array.isArray(value) ? value.filter(v => v !== option) : [];
                                                        //handleFamilyChange(field.id, newValue);
                                                    }
                                                }}
                                            />
                                            <label className="form-check-label" htmlFor={`family_${field.id}_${idx}`}>
                                                {option}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            );
                        // case 'gender':
                        //     return (
                        //         <select
                        //             className={`form-select ${fieldError ? 'is-invalid' : ''}`}
                        //             id={field.id}
                        //             value={value}
                        //             disabled
                        //         >
                        //             <option value="">Select gender</option>
                        //             {genderArr.map(opt => (
                        //                 <option key={opt} value={opt}>
                        //                     {opt}
                        //                 </option>
                        //             ))}
                        //         </select>
                        //     );
                        case 'time':
                            return (
                                <input
                                    type="datetime-local"
                                    className="form-control"
                                    id={field.id}
                                //value={value || getCurrentDateTime()} // Set current date and time as default
                                //onChange={e => handleFamilyChange(field.id, e.target.value)}
                                // required={field.required}
                                />
                            );
                        case 'text':
                            return (
                                <input
                                    type="text"
                                    className="form-control"
                                    id={field.id}
                                    value={value}
                                    //onChange={e => handleFamilyChange(field.id, e.target.value)}
                                    placeholder={field.helper}
                                // required={field.required}
                                />
                            );
                        case 'email':
                            return (
                                <div>
                                    <div className="input-group">
                                        <span className="input-group-text" style={{
                                            backgroundColor: '#f8f9fa',
                                            border: `1px solid ${fieldError ? '#dc3545' : '#d9d9d9'}`,
                                            borderRight: 'none'
                                        }}>
                                            <i className="fas fa-envelope" style={{ color: fieldError ? '#dc3545' : '#6c757d' }}></i>
                                        </span>
                                        <input
                                            type="email"
                                            className={`form-control ${fieldError ? 'is-invalid' : ''}`}
                                            id={field.id}
                                            value={value}
                                            //onChange={e => handleFamilyEmailChange(field.id, e.target.value)}
                                            // onBlur={e => handleFamilyEmailChange(field.id, e.target.value)}
                                            placeholder={field.helper}
                                            style={{
                                                borderLeft: 'none',
                                                borderColor: fieldError ? '#dc3545' : '#d9d9d9'
                                            }}
                                        />
                                    </div>
                                    {fieldError && (
                                        <div className="text-danger mt-1" style={{ fontSize: '0.875em' }}>
                                            {fieldError}
                                        </div>
                                    )}
                                </div>
                            );
                        case 'number': // Keep phone input for 'number' field type
                            return (
                                <div>
                                    <PhoneInput
                                        value={value}
                                        country="us" // Default country
                                        inputClass={`tel-input ${fieldError ? 'error' : ''}`} // Add error class
                                        onlyCountries={['us', 'in']} // Same countries as SignupFormUI
                                        enableAreaCodes
                                        placeholder={field.helper || "Enter phone number"}
                                        // onChange={(phone, countryData) =>
                                        //    handleFamilyPhoneChange(field.id, phone, countryData)
                                        // }
                                        inputProps={{
                                            name: field.id,
                                            required: false,
                                        }}
                                        containerStyle={{
                                            width: '100%'
                                        }}
                                        inputStyle={{
                                            width: '100%',
                                            height: '38px',
                                            fontSize: '14px',
                                            borderColor: fieldError ? '#dc3545' : '#d9d9d9', // Red border if error
                                        }}
                                    />
                                    {fieldError && (
                                        <div className="text-danger mt-1" style={{ fontSize: '0.875em' }}>
                                            {fieldError}
                                        </div>
                                    )}
                                </div>
                            );
                        case 'numeric': // New separate case for simple numeric input
                            return (
                                <input
                                    type="number"
                                    className="form-control"
                                    id={field.id}
                                    value={value}
                                    //onChange={e => handleFamilyChange(field.id, e.target.value)}
                                    placeholder={field.helper}
                                    step="any"
                                    min="0"
                                />
                            );
                        case 'date':
                            return (
                                <input
                                    type="date"
                                    className="form-control"
                                    id={field.id}
                                //value={value || getCurrentDate()}
                                //onChange={e => handleFamilyChange(field.id, e.target.value)}
                                // required={field.required}
                                />
                            );
                        case 'height':
                            {
                                const height = normalizeHeightValue(value);
                                return (
                                    <div className="row">
                                        <div className="col-6">
                                            <label>Feet</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                placeholder="Feet"
                                                value={height.feet}
                                                disabled
                                            />
                                        </div>
                                        <div className="col-6">
                                            <label>Inches</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                placeholder="Inches"
                                                value={height.inch}
                                                disabled
                                            />
                                        </div>
                                    </div>
                                );
                            }
                        case 'address':
                            {
                                const address = normalizeAddressValue(value);
                                return (
                                    <div className="row" style={{ background: '#f2f2f2' }}>
                                        <div className="form-group col-md-6">
                                            <label>Street</label>
                                            <input className="form-control" value={address.street} disabled />
                                        </div>
                                        <div className="form-group col-md-6">
                                            <label>City</label>
                                            <input className="form-control" value={address.city} disabled />
                                        </div>
                                        <div className="form-group col-md-6">
                                            <label>State</label>
                                            <input className="form-control" value={address.state} disabled />
                                        </div>
                                        <div className="form-group col-md-6">
                                            <label>Zip</label>
                                            <input className="form-control" value={address.zip} disabled />
                                        </div>
                                    </div>
                                );
                            }
                        case 'exercise':
                            {
                                const exercise = normalizeExerciseValue(value);
                                return (
                                    <>
                                        <div className="row mb-2">
                                            <div className="col-6">
                                                <label>Number of Sets</label>
                                                <input className="form-control" value={exercise.sets} disabled />
                                            </div>
                                            <div className="col-6">
                                                <label>Number of Reps</label>
                                                <input className="form-control" value={exercise.reps} disabled />
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-6">
                                                <label>Weight (lbs)</label>
                                                <input className="form-control" value={exercise.weight} disabled />
                                            </div>
                                            <div className="col-6">
                                                <label>Notes</label>
                                                <input className="form-control" value={exercise.notes} disabled />
                                            </div>
                                        </div>
                                    </>
                                );
                            }
                        default:
                            return (
                                <input
                                    type="text"
                                    className="form-control"
                                    id={field.id}
                                    value={value}
                                    //onChange={e => handleFamilyChange(field.id, e.target.value)}
                                    placeholder={field.helper}
                                // required={field.required}
                                />
                            );
                    }
                })()}
                {/* "Same as Primary Member" button */}
                {primaryValue !== undefined &&
                    primaryValue !== '' &&
                    primaryQuestionIdSet.has(normalizeQuestionId(field.id)) && (
                        <button
                            type="button"
                            className="btn btn-link p-0 mt-1"
                            style={{ fontSize: '0.95em' }}
                        // onClick={() => {
                        //     handleFamilyChange(field.id, primaryValue);
                        //     // Clear any existing error when copying from primary
                        //     setFieldErrors(prev => ({
                        //         ...prev,
                        //         [`family_${currentFamilyIndex}_${field.id}`]: ''
                        //     }));
                        // }}
                        >
                            Same as Primary Member
                        </button>
                    )}
            </div>
        );
    };

    const renderPrimaryFormField = field => {
        const value = responses.primary?.[field.id] ?? '';
        const fieldError = fieldErrors[`primary_${field.id}`]; // Get error for this field
        const isEducationField =
            normalizeQuestionId(field?.id).includes('education') ||
            String(field?.question || '').toLowerCase().includes('education') ||
            String(field?.helper || '').toLowerCase().includes('education');
        const dropdownPlaceholder = 'Select';
        //console.log('Rendering Primary Field:', field, 'with value:', value);

        // Get current time in HH:MM format for time fields
        const getCurrentTime = () => {
            const now = new Date();
            return now.toTimeString().slice(0, 5); // Returns "HH:MM"
        };

        // Get current date in YYYY-MM-DD format for date fields
        const getCurrentDate = () => {
            const now = new Date();
            return now.toISOString().split('T')[0]; // Returns "YYYY-MM-DD"
        };

        // Get current date and time in datetime-local format
        const getCurrentDateTime = () => {
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');
            const hours = String(now.getHours()).padStart(2, '0');
            const minutes = String(now.getMinutes()).padStart(2, '0');
            return `${year}-${month}-${day}T${hours}:${minutes}`;
        };

        switch (field.field) {
            case 'dropdown':
                return (
                    <select className="form-select" id={field.id} value={Array.isArray(value) ? value[0] || '' : value} disabled>
                        <option value="" disabled={isEducationField} hidden={isEducationField}>
                            {isEducationField ? '' : dropdownPlaceholder}
                        </option>
                        {(field.ddVal || []).map((opt, idx) => {
                            const optionValue = typeof opt === 'object' && opt !== null ? (opt.value ?? opt.label ?? opt.name ?? '') : opt;
                            const optionLabel = typeof opt === 'object' && opt !== null ? (opt.label ?? opt.value ?? opt.name ?? '') : opt;
                            return (
                                <option key={`${optionValue}_${idx}`} value={optionValue}>
                                    {optionLabel}
                                </option>
                            );
                        })}
                    </select>
                );
            case 'multiple-response':
                return (
                    <div>
                        {field.ddVal && field.ddVal.map((option, idx) => (
                            <div key={idx} className="form-check">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id={`primary_${field.id}_${idx}`}
                                    checked={Array.isArray(value) ? value.includes(option) : value === option}
                                // onChange={e => {
                                //     if (e.target.checked) {
                                //         const newValue = Array.isArray(value) ? [...value, option] : [option];
                                //         handlePrimaryChange(field.id, newValue);
                                //     } else {
                                //         const newValue = Array.isArray(value) ? value.filter(v => v !== option) : [];
                                //         handlePrimaryChange(field.id, newValue);
                                //     }
                                // }}
                                />
                                <label className="form-check-label" htmlFor={`primary_${field.id}_${idx}`}>
                                    {option}
                                </label>
                            </div>
                        ))}
                    </div>
                );
            // case 'gender':
            //     return (
            //         <select className="form-select" id={field.id} value={value} disabled>
            //             <option value="">Select gender</option>
            //             {genderArr.map(opt => (
            //                 <option key={opt} value={opt}>
            //                     {opt}
            //                 </option>
            //             ))}
            //         </select>
            //     );
            case 'time':
                return (
                    <input
                        type="datetime-local"
                        className="form-control"
                        id={field.id}
                    // value={value || getCurrentDateTime()} // Set current date and time as default
                    // onChange={e => handlePrimaryChange(field.id, e.target.value)}
                    // required={field.required}
                    />
                );
            case 'text':
                return (
                    <input
                        type="text"
                        className="form-control"
                        id={field.id}
                        value={value}
                        //onChange={e => handlePrimaryChange(field.id, e.target.value)}
                        placeholder={field.helper}
                    // required={field.required}
                    />
                );
            case 'email':
                return (
                    <div>
                        <div className="input-group">
                            <span className="input-group-text" style={{
                                backgroundColor: '#f8f9fa',
                                border: `1px solid ${fieldError ? '#dc3545' : '#d9d9d9'}`,
                                borderRight: 'none'
                            }}>
                                <i className="fas fa-envelope" style={{ color: fieldError ? '#dc3545' : '#6c757d' }}></i>
                            </span>
                            <input
                                type="email"
                                className={`form-control ${fieldError ? 'is-invalid' : ''}`}
                                id={field.id}
                                value={value}
                                // onChange={e => handlePrimaryEmailChange(field.id, e.target.value)}
                                // onBlur={e => handlePrimaryEmailChange(field.id, e.target.value)}
                                placeholder={field.helper}
                                style={{
                                    borderLeft: 'none',
                                    borderColor: fieldError ? '#dc3545' : '#d9d9d9'
                                }}
                            />
                        </div>
                        {fieldError && (
                            <div className="text-danger mt-1" style={{ fontSize: '0.875em' }}>
                                {fieldError}
                            </div>
                        )}
                    </div>
                );
            case 'number': // Keep phone input for 'number' field type
                return (
                    <div>
                        <PhoneInput
                            value={value}
                            country="us" // Default country
                            inputClass={`tel-input ${fieldError ? 'error' : ''}`} // Add error class
                            onlyCountries={['us', 'in']} // Same countries as SignupFormUI
                            enableAreaCodes
                            placeholder={field.helper || "Enter phone number"}
                            // onChange={(phone, countryData) =>
                            //     handlePrimaryPhoneChange(field.id, phone, countryData)
                            // }
                            inputProps={{
                                name: field.id,
                                required: false,
                            }}
                            containerStyle={{
                                width: '100%'
                            }}
                            inputStyle={{
                                width: '100%',
                                height: '38px',
                                fontSize: '14px',
                                borderColor: fieldError ? '#dc3545' : '#d9d9d9', // Red border if error
                            }}
                        />
                        {fieldError && (
                            <div className="text-danger mt-1" style={{ fontSize: '0.875em' }}>
                                {fieldError}
                            </div>
                        )}
                    </div>
                );
            case 'numeric': // New separate case for simple numeric input
                return (
                    <input
                        type="number"
                        className="form-control"
                        id={field.id}
                        value={value}
                        //onChange={e => handlePrimaryChange(field.id, e.target.value)}
                        placeholder={field.helper}
                        step="any"
                        min="0"
                    />
                );
            case 'date':
                return (
                    <input
                        type="date"
                        className="form-control"
                        id={field.id}
                    // value={value || getCurrentDate()} // Set current date as default
                    // onChange={e => handlePrimaryChange(field.id, e.target.value)}
                    // required={field.required}
                    />
                );
            case 'height':
                {
                    const height = normalizeHeightValue(value);
                    return (
                        <div className="row">
                            <div className="col-6">
                                <label>Feet</label>
                                <input className="form-control" value={height.feet} disabled />
                            </div>
                            <div className="col-6">
                                <label>Inches</label>
                                <input className="form-control" value={height.inch} disabled />
                            </div>
                        </div>
                    );
                }
            case 'address':
                {
                    const address = normalizeAddressValue(value);
                    return (
                        <div className="row" style={{ background: '#f2f2f2' }}>
                            <div className="form-group col-md-6">
                                <label>Street</label>
                                <input className="form-control" value={address.street} disabled />
                            </div>
                            <div className="form-group col-md-6">
                                <label>City</label>
                                <input className="form-control" value={address.city} disabled />
                            </div>
                            <div className="form-group col-md-6">
                                <label>State</label>
                                <input className="form-control" value={address.state} disabled />
                            </div>
                            <div className="form-group col-md-6">
                                <label>Zip</label>
                                <input className="form-control" value={address.zip} disabled />
                            </div>
                        </div>
                    );
                }
            case 'exercise':
                {
                    const exercise = normalizeExerciseValue(value);
                    return (
                        <>
                            <div className="row mb-2">
                                <div className="col-6">
                                    <label>Number of Sets</label>
                                    <input className="form-control" value={exercise.sets} disabled />
                                </div>
                                <div className="col-6">
                                    <label>Number of Reps</label>
                                    <input className="form-control" value={exercise.reps} disabled />
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-6">
                                    <label>Weight (lbs)</label>
                                    <input className="form-control" value={exercise.weight} disabled />
                                </div>
                                <div className="col-6">
                                    <label>Notes</label>
                                    <input className="form-control" value={exercise.notes} disabled />
                                </div>
                            </div>
                        </>
                    );
                }
            default:
                return (
                    <input
                        type="text"
                        className="form-control"
                        id={field.id}
                        value={value}
                        // onChange={e => handlePrimaryChange(field.id, e.target.value)}
                        placeholder={field.helper}
                    // required={field.required}
                    />
                );
        }


    };

    const familyMembers = useMemo(() => {
        if (!formData?.companyData?.member?.familyMembers) return [];
        return formData.companyData.member.familyMembers;
    }, [formData]);
    return (
        <div className="member-registration">
            <div className="container-fluid">
                <div className="row">
                    <div className="col-12">
                        <div className="page-content bg-white min-vh-100">
                            <div className="container">
                                <div className="row justify-content-center">
                                    <div className="col-md-12 col-lg-11 col-xl-10">
                                        <div className="card registration-card shadow-lg registration-card-container">
                                            <div className="card-body p-5">
                                                <div className="text-center mb-4">
                                                    <div className="company-logo-circle mx-auto mb-3">
                                                        {companyLogo ? (
                                                            <img
                                                                src={companyLogo}
                                                                alt={registrationCompany.name || 'Company Logo'}
                                                                className="rounded-circle img-fluid"
                                                                style={{
                                                                    width: '150px',
                                                                    height: '150px',
                                                                    objectFit: 'cover',
                                                                    border: '4px solid rgb(229, 231, 233)',
                                                                    padding: '8px',
                                                                }}
                                                            />
                                                        ) : (
                                                            <img
                                                                src="https://coachfirststatic.s3.amazonaws.com/images/new-logo.png"
                                                                alt="CoachFirst"
                                                                className="rounded-circle img-fluid"
                                                                style={{
                                                                    width: '150px',
                                                                    height: '150px',
                                                                    objectFit: 'cover',
                                                                    border: '4px solid rgb(229, 231, 233)',
                                                                    padding: '8px',
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                    <h5 className="text-danger mb-1">
                                                        {registrationCompany?.name || 'Company'}
                                                    </h5>
                                                    <h3 className="text-center mb-4 text-dark">
                                                        Member Information Form
                                                    </h3>
                                                </div>
                                                {formError && (
                                                    <div className="alert alert-danger" role="alert">
                                                        {formError}
                                                    </div>
                                                )}

                                                <div className="member-questionnaire">
                                                    {showFamilyForm ? (
                                                        <div className="registration-form-content">
                                                            <h5 className="mb-3">
                                                                Family Member {currentFamilyIndex + 1} of {familyMembers.length}
                                                                <span className="ms-2 text-muted">
                                                                    ({familyMembers[currentFamilyIndex]?.firstName} {familyMembers[currentFamilyIndex]?.lastName}, Age: {calculateAge(familyMembers[currentFamilyIndex]?.dob)})
                                                                </span>
                                                            </h5>
                                                            <form onSubmit={e => {
                                                                e.preventDefault();
                                                                if (currentFamilyIndex < familyMembers.length - 1) {
                                                                    setCurrentFamilyIndex(i => {
                                                                        const nextIndex = i + 1;
                                                                        setTimeout(() => {
                                                                            // Only scroll if there is no error
                                                                            if (!formError) {
                                                                                window.scrollTo({ top: 0, behavior: 'smooth' });
                                                                            }
                                                                        }, 0);
                                                                        return nextIndex;
                                                                    });
                                                                } else {
                                                                    //handleSubmit(e);
                                                                }
                                                            }}>
                                                                {/* Grouped questions by age */}
                                                                {(() => {
                                                                    const fm = familyMembers[currentFamilyIndex];
                                                                    const age = calculateAge(fm.dob);
                                                                    const { questionsArray } = getFamilyQuestionsByAge(fm);
                                                                    return (
                                                                        <>
                                                                            {/* {age >= 13 && plus13.length > 0 && (
                                                                                <> */}
                                                                            {/* <h6 className="mt-3 text-success">
                                                                                        Questions for Family Member 13+
                                                                                    </h6> */}
                                                                            <div className="row">
                                                                                {questionsArray.map(field => (
                                                                                    <div key={field.id} className="col-12 mb-3">
                                                                                        <label htmlFor={field.id} className="form-label">
                                                                                            {field.question}
                                                                                            {/* {field.required && <span className="text-danger ms-1">*</span>} */}
                                                                                        </label>
                                                                                        {renderFamilyFormField(field)}
                                                                                        {field.helper && field.field !== 'address' && (
                                                                                            <div className="form-text text-muted">{field.helper}</div>
                                                                                        )}
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                            {/* </>
                                                                            )} */}
                                                                        </>
                                                                    );
                                                                })()}
                                                                <div className="d-grid gap-2 mt-4">
                                                                    <div className="d-flex justify-content-between">
                                                                        {currentFamilyIndex === 0 ? (
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-outline-secondary"
                                                                                onClick={() => setShowFamilyForm(false)}
                                                                            >
                                                                                Previous: Primary Member
                                                                            </button>
                                                                        ) : (
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-outline-secondary"
                                                                                onClick={() => setCurrentFamilyIndex(i => i - 1)}
                                                                            >
                                                                                Previous Family Member
                                                                            </button>
                                                                        )}
                                                                        <button type="submit" className="btn btn-danger btn-lg ms-auto" disabled={formLoading}>
                                                                            {currentFamilyIndex < familyMembers.length - 1 ? 'Next Family Member' : 'Submit All'}
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </form>
                                                        </div>
                                                    ) : (
                                                        <div className="registration-form-content">
                                                            <div className="d-flex align-items-center justify-content-start mb-3">
                                                                <h5 className="text-dark mb-0 me-2">
                                                                    Please complete the following information for
                                                                    <span className="ms-2 text-muted">
                                                                        (Member Name, Age: {calculateAge(formData?.companyData?.member?.dob)})
                                                                    </span>
                                                                </h5>
                                                                <Tooltip
                                                                    placement="top"
                                                                    title="Information for any Family Members will be completed on the next page"
                                                                >
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-info btn-sm rounded-circle p-1"
                                                                        style={{
                                                                            width: '28px',
                                                                            height: '28px',
                                                                            border: '1px solid #0dcaf0',
                                                                        }}
                                                                    >
                                                                        <i className="fas fa-info" style={{ fontSize: '12px' }}></i>
                                                                    </button>
                                                                </Tooltip>
                                                            </div>
                                                            <form onSubmit={e => {
                                                                e.preventDefault();
                                                                if (familyMembers.length > 0) {
                                                                    setShowFamilyForm(true);
                                                                } else {
                                                                    //handleSubmit(e);
                                                                }
                                                            }}>
                                                                <div className="row">
                                                                    {formQuestions.map((field) => (
                                                                        <div key={field.id} className="col-12 mb-3">
                                                                            <label htmlFor={field.id} className="form-label">
                                                                                {field.question}
                                                                                {/* {field.required && <span className="text-danger ms-1">*</span>} */}
                                                                            </label>
                                                                            {renderPrimaryFormField(field)}
                                                                            {field.helper && field.field !== 'address' && (
                                                                                <div className="form-text text-muted">{field.helper}</div>
                                                                            )}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                                <div className="d-grid gap-2 mt-4">
                                                                    <button type="submit" className="btn btn-danger btn-lg" disabled={formLoading}>
                                                                        {formLoading ? (
                                                                            <>
                                                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                                                Submitting...
                                                                            </>
                                                                        ) : (
                                                                            familyMembers.length > 0 ? 'Next: Family Member(s)' : 'Submit & Go to Dashboard'
                                                                        )}
                                                                    </button>
                                                                </div>
                                                            </form>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
